import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { CardNotification, Select } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import linkLogEntriesQuery from "../../graphql/linkLogEntries.query.graphql";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import Layout from "@common/layout/Layout";
import LinkHistoryTable from "@modules/history-changes/link/components/link-history-table";
import LinkLogReportModal from "@modules/history-changes/link/components/link-log-report-modal";
import { toUtcDateRange } from "@modules/history-changes/link/utils/linkLogReport.util";
import ChangesHistoryResults from "@modules/common/changes-history-results";
import { IParams } from "@models/IParams";
import {
  DataQuery,
  PageInfo,
  LinkLogEntry,
  LinkLogEntryFilterFieldsInput,
  OrderByDirection,
} from "@models/ISchema";
import { dateRangeValues, getDateRangeOptions } from "@utils/days";
import styles from "./linkChangehistory.module.scss";

const defaultOrderBy = {
  direction: OrderByDirection.Desc,
  field: "date",
  header: "",
};

const defaultSearchBy = {
  fields: [],
  text: "",
};

/**
 * LinkChangeHistory component
 *
 * Displays and manages link modification history with filtering, sorting and export capabilities.
 * Integrates with Apollo Client for data fetching and maintains local state for filters and pagination.
 */
const LinkChangeHistory = () => {
  const { scenario, origin }: IParams = useParams();
  const client = useApolloClient();
  const [logEntries, setLogEntries] = useState<LinkLogEntry[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSelfChanges, setShowSelfChanges] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [columnsToSearchBy, setColumnsToSearchBy] = useState({
    Fecha: true,
    Liga: true,
    "Recurso ID": true,
    Responsable: true,
  });
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [searchBy, setSearchBy] = useState(defaultSearchBy);
  const [dateRangeValue, setDateRangeValue] = useState<"all" | keyof typeof dateRangeValues>(
    "last7daysRange",
  );
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [sizePerPage, setSizePerPage] = useState(20);

  const token = localStorage?.getItem("token") ?? "";
  const { getLinkLogEntriesReportUrl, adaptedHomeUrl } = useNavigationUrl();

  // Memo to keep the date range option in the same language
  const dateRangeOption = useMemo(() => {
    return getDateRangeOptions().find(option => option.value === dateRangeValue);
  }, [dateRangeValue, language]);

  const dateRange = useMemo(() => {
    if (dateRangeValue === "all") {
      return undefined;
    }

    return dateRangeValues[dateRangeValue];
  }, [dateRangeOption]);

  /**
   * Fetches link history entries from GraphQL API
   *
   * Constructs query variables based on current filters and pagination settings.
   * Updates component state with fetched data and manages loading state.
   */
  const getLogEntries = async (
    page = 1,
    newOrderBy = orderBy as any,
    searchBy = defaultSearchBy as any,
  ) => {
    setIsLoading(true);
    setOrderBy(newOrderBy);
    setSearchBy(searchBy);

    const variables = {
      query: linkLogEntriesQuery,
      variables: {
        scenarioId: scenario,
        originId: origin,
        filter: {
          fields: {
            ...(showSelfChanges && { isOwner: { is: true } }),
            ...(dateRange && { dateRange }),
          },
          searchBy:
            !!searchBy?.text && !!searchBy?.fields?.length
              ? {
                  fields: searchBy?.fields ?? [],
                  text: searchBy?.text ?? "",
                }
              : undefined,
          orderBy: !!newOrderBy?.field
            ? {
                field: newOrderBy?.field,
                direction: newOrderBy?.direction ?? "ASC",
              }
            : undefined,
          pagination: {
            page,
            size: sizePerPage,
          },
        },
      },
    };
    const { data } = await client.query(variables);
    const dataQuery = data?.data as DataQuery;

    setLogEntries(dataQuery?.linkLogEntry?.items ?? []);
    setPageInfo(dataQuery?.linkLogEntry?.pageInfo);

    setIsLoading(false);
    !hasInitialLoad && setHasInitialLoad(true);
  };

  // Load the first page on mount
  useEffect(() => {
    getLogEntries();
  }, []);

  // React to filter changes: user's own changes toggle or page size adjustment
  useEffect(() => {
    if (hasInitialLoad) {
      getLogEntries(1, orderBy, searchBy);
    }
  }, [showSelfChanges, sizePerPage, dateRangeValue]);

  const lastUpdateDateTime = dayjs().format("DD/MM/YYYY HH:mm");

  /**
   * Builds the report URL with the current filters
   *
   * Conditionally includes user ID filter when 'Show My Changes' is enabled.
   * The date range is sent in UTC, as the backend expects.
   */
  const getReportUrl = () => {
    const stringifiedFilters = JSON.stringify(
      {
        fields: {
          isOwner: showSelfChanges ? { is: true } : undefined,
          dateRange: toUtcDateRange(dateRange),
        },
      } as LinkLogEntryFilterFieldsInput,
      null,
    );

    return `${getLinkLogEntriesReportUrl(token)}&filter=${encodeURIComponent(stringifiedFilters)}`;
  };

  const handleDownloadReport = () => {
    setShowReportModal(true);
  };

  /**
   * Refreshes data with current filter settings
   *
   * Maintains existing sort and search parameters while resetting to first page.
   */
  const handleUpdateView = () => {
    getLogEntries(1, orderBy, searchBy);
  };

  const dynamicTitle =
    pageInfo?.total !== 1
      ? t("link-changes-history.header.title-plural")
      : t("link-changes-history.header.title");

  return (
    <Layout contextSearch>
      <ChangesHistoryResults
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
        titleSnackbar={t("link-changes-history.header.snackbar.title")}
        subtitle={`${pageInfo?.total} ${dynamicTitle}`}
        title={t("link-changes-history.header.subtitle")}
        loading={isLoading}
        labelSwitch={t("link-changes-history.switch.title")}
        breadcrumbItems={[
          {
            title: t("link-changes-history.breadcrumb.prev"),
            path: adaptedHomeUrl(),
          },
          {
            title: t("link-changes-history.breadcrumb.current"),
          },
        ]}
        hasInitialLoad={hasInitialLoad}
        lastUpdateDateTime={lastUpdateDateTime}
        handleDownloadReport={handleDownloadReport}
        handleUpdateView={handleUpdateView}
        labelLastUpdate={t("link-changes-history.header.last-update")}
        showSelfChanges={showSelfChanges}
        setShowSelfChanges={setShowSelfChanges}
        buttonDownloadReportLabel={t("link-changes-history.header.btn-download-report")}
        buttonUpdateViewLabel={t("link-changes-history.header.btn-update-view")}
        filters={
          <Select
            className={styles.dateRangeSelector}
            value={dateRangeOption}
            onChange={({ value }) => setDateRangeValue(value)}
            options={getDateRangeOptions()}
          />
        }
      >
        {!logEntries?.length ? (
          <CardNotification
            title={t("link-changes-history.notifications.empty-results.title")}
            state="info"
            outlined
          >
            {t("link-changes-history.notifications.empty-results.description")}
          </CardNotification>
        ) : (
          <LinkHistoryTable
            data={logEntries}
            orderBy={orderBy}
            searchBy={searchBy}
            onLoadLogEntries={getLogEntries}
            columnsToSearchBy={columnsToSearchBy}
            setColumnsToSearchBy={setColumnsToSearchBy}
            onChangeSizePerPage={setSizePerPage}
            pagination={{
              page: pageInfo?.page,
              size: sizePerPage,
              total: pageInfo?.total,
            }}
          />
        )}
      </ChangesHistoryResults>
      {showReportModal && (
        <LinkLogReportModal
          isOpen={showReportModal}
          reportUrl={getReportUrl()}
          periodLabel={dateRangeOption?.label ?? ""}
          onClose={() => setShowReportModal(false)}
          onDownloaded={() => setShowSnackbar(true)}
        />
      )}
    </Layout>
  );
};

export default LinkChangeHistory;
